import React, { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../store/hooks/hooks";
import type { IDDItem } from "../../components/common/DropdownMenu";
import { InputRow } from "./InputRow";
import DropdownMenu from "../../components/common/DropdownMenu";
import { DownloadButton } from "../../components/common/DownloadButton";
import { CalculateButton } from "../../components/common/CalculateButton";
import { setInputValue, setGInputValue, setGasCompositionInputValue, updateInputByUnit, validateInputs, validateGInputs, validateGasCompositionInputs, setCriteria, setStrategy } from "../../store/slices/pvtCalculatorSlice";
import type { IPVTCalculator, PVTCriteriaOption, PVTStrategyOption } from "../../interfaces/interfaces";
import { useCalculatorMutation } from "../../store/slices/apiSlice";
import { UnitConverter } from "../../components/MeasurementUnits/UnitConverter";

const EMPTY_INPUT_IDS: string[] = [];
const converter = new UnitConverter();

const InputsSection: React.FC = () => {
	const calculator = useAppSelector(state => state.pvtCalculator);

	const inputIds = useMemo(() => {
		if (!calculator) {
			return EMPTY_INPUT_IDS;
		}

		return calculator.inputs.map(input => input.id);
	}, [calculator, calculator.inputs]);

	const gInputIds = useMemo(() => {
		if (!calculator) {
			return EMPTY_INPUT_IDS;
		}

		return calculator.gInputs.map(input => input.id);
	}, [calculator.gInputs]);

	const gasComposInputIds = useMemo(() => {
		if (!calculator) {
			return EMPTY_INPUT_IDS;
		}

		return calculator.gasCompositionInputs.map(input => input.id);
	}, [calculator, calculator.gasCompositionInputs]);

	const unitsDDItems: { [key: string]: IDDItem[] } = useMemo(() => {
		if (!calculator) {
			return {};
		}

		return calculator.inputs.reduce<Record<string, IDDItem[]>>((acc, input) => {
			const units = converter.getUnitsByCategory(input.unitCategoryId);
			acc[input.id] = units.map(unit => ({
				id: unit.id,
				translationKey: `pvt:measurement_units.${unit.category}.${unit.id}`
			} as IDDItem));
			return acc;
		}, {});
	}, [calculator.inputs.map(i => i.currentUnitId).join(',')]);

	const criteriaOptions = useMemo(() => {
		if (!calculator) {
			return [];
		}
		return calculator.criteria.options.map(option => ({ id: option, translationKey: `pvt:criteria_options.${option}` } as IDDItem));
	}, [calculator, calculator.criteria.options]);

	const dispatch = useAppDispatch();
	const handleInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(setInputValue({ inputName, value }));
		},
		[dispatch]);

	const handleGInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(setGInputValue({ inputName, value }));
		},
		[dispatch]);

	const handleGasCompositionInputChange = useCallback(
		(inputName: string, value: number | null) => {
			dispatch(setGasCompositionInputValue({ inputName, value }));
		},
		[dispatch]);

	const handleSelectUnit = useCallback((inputId: string, selectedUnitId: string) => {
		dispatch(updateInputByUnit({ inputId, selectedUnitId }));
	}, [dispatch]);

	const validated = (freshCalc: IPVTCalculator) => {
		if (freshCalc.inputs.some(input => input.error != null))
			return false;

		if (freshCalc.strategy.selected == "gascompose" &&
			(freshCalc.gasCompositionInputs.some(input => input.error != null) || freshCalc.gasCompositionError))

			return false;

		if (freshCalc.strategy.selected == "g" && (freshCalc.gInputs.some(input => input.error != null)))
			return false;

		return true;
	};

	const [triggerCalculator, { isLoading: isCalculatorLoading, isSuccess }] = useCalculatorMutation();

	const getFormData = useCallback(() => {
		if (calculator.criteria.selected === undefined) {
			console.error('Calculator state is missing or criteria not selected!');
			return;
		}

		dispatch(validateInputs());
		dispatch(validateGInputs());
		dispatch(validateGasCompositionInputs());

		if (!validated(calculator))
			return;

		const formData: Record<string, string | number> = {};
		calculator.inputs.forEach(input => {
			formData[input.queryName] = input.baseValue ?? 0;
		});

		formData.criteria = calculator.criteria.selected;
		if (calculator.strategy.selected === "gascompose") {
			calculator.gasCompositionInputs.forEach(input => {
				formData[input.queryName] = input.baseValue ?? 0;
			});
		}
		else if (calculator.strategy.selected === "g") {
			calculator.gInputs.forEach(input => {
				formData[input.queryName] = input.baseValue ?? 0;
			});
		}

		return formData;
	}, [calculator, dispatch]);

	const onCalculate = useCallback(async () => {
		const formData = getFormData();

		if (formData) {
			triggerCalculator({
				calculator_type: "pvt",
				...formData,
			});
		}
	}, [triggerCalculator, getFormData]);

	const handleSelectCriteria = useCallback((selectedCriteria: string) => {
		if (!(selectedCriteria as PVTCriteriaOption)) {
			return;
		}

		dispatch(setCriteria(selectedCriteria as PVTCriteriaOption));
	}, [dispatch, calculator]);

	const handleSelectStrategy = useCallback((selectedStrategy: string) => {
		if (!(selectedStrategy as PVTStrategyOption)) {
			return;
		}

		dispatch(setStrategy(selectedStrategy as PVTStrategyOption));
	}, [dispatch, calculator]);

	const { t } = useTranslation('pvt');
	return (<div className="pvt__inputs-section">
		<fieldset className="pvt__form">
			<legend>{t('main_parameters')}</legend>
			<div className="pvt__first_column">
				{inputIds.map((inputId) => (
					<InputRow
						key={inputId}
						inputId={inputId}
						dropdownItems={unitsDDItems[inputId]}
						onInputChange={handleInputChange}
						onSelectUnit={handleSelectUnit}
					/>
				))}
				<div className="pvt__criteria">
					<DropdownMenu
						inputId={calculator.criteria.id}
						items={criteriaOptions}
						selectedItemId={calculator.criteria.selected}
						onSelect={handleSelectCriteria}
					/>
				</div>
			</div>

			<hr className="pvt__section-divider" />

			<div className="pvt__second_column">
				<div className="pvt__strategy">
					<DropdownMenu
						inputId={calculator.strategy.id}
						items={calculator.strategy.options.map(option => ({ id: option, translationKey: `pvt:strategy_options.${option}` } as IDDItem))}
						selectedItemId={calculator.strategy.selected}
						onSelect={handleSelectStrategy}
					/>
				</div>

				{calculator.strategy.selected && calculator.strategy.selected == "g" &&
					gInputIds.map((inputId) => (
						<InputRow
							key={inputId}
							inputId={inputId}
							dropdownItems={unitsDDItems[inputId]}
							onInputChange={handleGInputChange}
							onSelectUnit={handleSelectUnit}
						/>
					))
				}

				{calculator.strategy.selected && calculator.strategy.selected == "gascompose" &&
					<>
						<div className="pvt__gas-compos-header">
							{/* <span>{t('strategy_options.gascompose')}</span> */}
							{/* <span>{t('molar_fractions')}</span> */}
						</div>
						<div className="pvt__gas-compos-grid">
							{gasComposInputIds.map((inputId) => (
								<InputRow
									key={inputId}
									inputId={inputId}
									dropdownItems={unitsDDItems[inputId]}
									onInputChange={handleGasCompositionInputChange}
									onSelectUnit={handleSelectUnit}
								/>
							))}
						</div>
						{calculator.gasCompositionError && (
							<span className="pvt__validation-error">
								{t(calculator.gasCompositionError, { value: calculator.gasCompositionInputs.reduce((acc, input) => acc + (input.currentValue ?? 0), 0).toFixed(5) })}</span>
						)}
					</>
				}
			</div>
		</fieldset>
		<div className="pvt__buttons">
			<CalculateButton onClick={onCalculate} disabled={isCalculatorLoading} />
			<DownloadButton getFormData={getFormData} calculatorType="pvt" disabled={false} title={t(`download`)} />
		</div>
	</div>
	);
};

export default memo(InputsSection);
